import { Link } from "@/lib/router-compat";
import BrandLogo from "@/components/markets/BrandLogo";

export interface Quote {
  symbol: string;
  name: string;
  price: number;
  change_7d: number;
  series: Array<[number, number]>;
}

export function QuoteSparkline({ points, tone }: { points: number[]; tone: "up" | "down" }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const d = points
    .map((p, i) => `${(i / (points.length - 1)) * 100},${28 - ((p - min) / span) * 26}`)
    .join(" L ");
  const stroke = tone === "up" ? "hsl(var(--success))" : "hsl(var(--destructive))";
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="mt-3 h-8 w-full">
      <path d={`M ${d}`} fill="none" stroke={stroke} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Large index / headline quote card, linked to the equity detail page. */
export function QuoteIndexCard({ q }: { q: Quote }) {
  const up = q.change_7d >= 0;
  return (
    <Link
      to={`/markets/stocks/${q.symbol}`}
      className="block rounded-2xl border border-border bg-card p-4 transition-colors hover:border-foreground/20 hover:bg-muted/40"
    >
      <div className="flex items-center gap-2">
        <BrandLogo symbol={q.symbol} name={q.name} size={22} />
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{q.name}</div>
      </div>
      <div className="mt-1.5 text-xl font-semibold tabular-nums text-foreground">
        {q.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </div>
      <div className={`text-sm tabular-nums ${up ? "text-success" : "text-destructive"}`}>
        {up ? "+" : ""}
        {q.change_7d.toFixed(2)}% 7d
      </div>
      <QuoteSparkline points={q.series?.map(([, v]) => v) ?? []} tone={up ? "up" : "down"} />
    </Link>
  );
}

/** Compact ticker row used for tech equities grids. */
export function QuoteTickerRow({ q }: { q: Quote }) {
  const up = q.change_7d >= 0;
  return (
    <Link
      to={`/markets/stocks/${q.symbol}`}
      className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2 transition-colors hover:border-foreground/20 hover:bg-muted/40"
    >
      <div className="flex items-center gap-2">
        <BrandLogo symbol={q.symbol} name={q.name} size={24} />
        <div>
          <div className="font-mono text-xs font-semibold">{q.symbol}</div>
          <div className="text-[11px] text-muted-foreground">{q.name}</div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm tabular-nums">
          ${q.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
        <div className={`text-[11px] tabular-nums ${up ? "text-success" : "text-destructive"}`}>
          {up ? "+" : ""}
          {q.change_7d.toFixed(2)}%
        </div>
      </div>
    </Link>
  );
}
